import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'

import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'

import columns from './columns'

import CreateFeatureModal from '../modals/CreateFeatureModal'

import Button from '~/components/Buttons/Button'
import ContentLayout from '~/components/ContentLayout'
import Header from '~/components/Header/Classic'
import Layout from '~/components/Layouts/Layout'
import ModalDialog from '~/components/ModalDialog'
import Section from '~/components/Section'
import TableWrapper from '~/components/Tables/TableWrapper'
import ViewTitle from '~/components/ViewTitle'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useAppSummary } from '~/providers/AppSummary'
import { useDeleteFeatureMutation, useFeaturesListQuery, iapKeys } from '~/queries/iap'
import { transformByDeviceType } from '~/utils/application'
import { displayToast } from '~/utils/toast'

const ENTRIES_NAME = 'in-apps-features-list'

const FeaturesView = (): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const { appDetails, appKey, deviceType, isLoading: isAppSummaryLoading } = useAppSummary()

  const gridRef = useRef<AgGridReact>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const [featureIdToDelete, setFeatureIdToDelete] = useState('')

  /** Queries & Mutations */

  const { data: features, isLoading: isFeaturesLoading } = useFeaturesListQuery({ appKey, deviceType })

  const { mutateAsync: deleteFeature } = useDeleteFeatureMutation()

  /** Handlers */

  const handleModalState = (isOpen: boolean, modalType: 'create' | 'delete') => () =>
    modalType === 'create' ? setIsCreateModalOpen(isOpen) : setIsDeleteModalOpen(isOpen)

  const handleCreateFeatureClick = () => setIsCreateModalOpen(true)

  const handleDeleteFeatureClick = (featureId: string) => {
    setFeatureIdToDelete(featureId)
    setIsDeleteModalOpen(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      await deleteFeature({
        appKey,
        deviceType,
        featureId: featureIdToDelete,
      })
      await queryClient.invalidateQueries({
        queryKey: iapKeys.featuresList(appKey, deviceType),
      })
      setIsDeleteModalOpen(false)
      setFeatureIdToDelete('')
      displayToast('Feature successfully deleted', 'success')
    } catch (e) {
      captureException(e)
      displayToast('Feature could not be deleted', 'error')
    }
  }

  /** Effects */

  useDocumentTitle('IAP - Features')

  /** Render */

  const appSelectorApp = appDetails ? transformByDeviceType(appDetails, deviceType) : undefined

  const isSwitchDisabled = !appDetails?.android || !appDetails?.ios

  return (
    <>
      <Header apps={appSelectorApp ? [appSelectorApp] : []} isSwitchDisabled={isSwitchDisabled} />
      <Layout className="in-apps-features-view" withPadding>
        <ViewTitle>
          <span>Features</span>
        </ViewTitle>
        <ContentLayout loading={isAppSummaryLoading && isFeaturesLoading}>
          <Section>
            <TableWrapper
              buttonComponent={<Button onClick={handleCreateFeatureClick}>Create new feature</Button>}
              columnDefs={columns}
              context={{ handleDeleteFeatureClick }}
              entriesName={ENTRIES_NAME}
              ref={gridRef}
              rowData={features?.data}
              tableRef={tableRef}
              withPagination={false}
              withSearchBar
            />
          </Section>
        </ContentLayout>
      </Layout>
      <ModalDialog
        isOpen={isDeleteModalOpen}
        mainText="Do you really want to delete this feature?"
        onClose={handleModalState(false, 'delete')}
        onConfirm={handleDeleteConfirm}
      />
      <CreateFeatureModal
        appKey={appKey}
        deviceType={deviceType}
        isOpen={isCreateModalOpen}
        onClose={handleModalState(false, 'create')}
      />
    </>
  )
}

export default FeaturesView
